import {
  BlockOutlined,
  DoNotDisturbAltOutlined
} from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";

import MetricCard from "../../components/MetricCard";
import UnprocessedReasonsTable from "../../components/UnprocessedReasonsTable";
import { useUnprocessedCallsInsights } from "../../data/hooks/useUnprocessedCallsInsights";
import { formatDuration } from "../../utils/date";
import { TimeRange } from "../../utils/timerange";
import { convertFiltersToURLSearchParams } from "./utils";

interface UnprocessedCallsSectionProps {
  timeRange: TimeRange;
  timezone: string;
  protocol: string;
  user: string;
  callType: string;
  permittedEmails: string[] | null;
}

const UnprocessedCallsSection = ({
  timeRange,
  timezone,
  protocol,
  user,
  callType,
  permittedEmails
}: UnprocessedCallsSectionProps) => {
  const navigate = useNavigate();

  const { data, isFetching: isLoading } = useUnprocessedCallsInsights({
    timeRange,
    timezone,
    protocol,
    user,
    callType,
    permittedEmails
  });

  const unprocessedCalls = data?.unprocessedCalls || [];
  const totalUnprocessedCount = unprocessedCalls.reduce((sum, item) => sum + item.count, 0);
  const totalUnprocessedDuration = unprocessedCalls.reduce((sum, item) => sum + item.totalDurationMs, 0);
  const unscoredSummary = data?.unscoredSummary || { count: 0, totalDurationMs: 0 };

  const handleUnprocessedClick = () => {
    const searchParams = convertFiltersToURLSearchParams({
      timeRange,
      sentiment: "all",
      relevance: "all",
      risk: "all",
      protocol,
      user,
      processDecision: "DO_NOT_PROCESS",
      callType
    });
    navigate(`/explore?${searchParams.toString()}`);
  };

  const handleUnscoredClick = () => {
    const searchParams = convertFiltersToURLSearchParams({
      timeRange,
      sentiment: "all",
      relevance: "all",
      risk: "all",
      protocol,
      user,
      processDecision: "DO_PROCESS",
      callType
    });
    searchParams.set("scored_status", "unscored");
    navigate(`/explore?${searchParams.toString()}`);
  };

  return (
    <>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <MetricCard
            icon={<BlockOutlined />}
            title="Unprocessed Calls"
            value={totalUnprocessedCount.toLocaleString()}
            sublabel={`${formatDuration(totalUnprocessedDuration)} total`}
            tooltip="Calls that were not processed for call summarization, due to the reasons classified below."
            onClick={!isLoading ? handleUnprocessedClick : undefined}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <MetricCard
            icon={<DoNotDisturbAltOutlined />}
            title="Unscored Calls"
            value={unscoredSummary.count.toLocaleString()}
            sublabel={`${formatDuration(unscoredSummary.totalDurationMs)} total`}
            tooltip="Calls that were processed (summarized) but not analyzed for scoring, since there was no appropriate lens for the call's content."
            onClick={!isLoading ? handleUnscoredClick : undefined}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12 }}>
          <UnprocessedReasonsTable
            timeRange={timeRange}
            protocol={protocol}
            user={user}
            callType={callType}
            unprocessedCalls={unprocessedCalls}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default UnprocessedCallsSection;
